import { useCallback, useEffect, useMemo, useState } from 'react';
import { Checkbox, Flex, Form, Modal, Steps, Typography } from 'antd';
import { useAuth } from '@contexts/auth';
import type { WmsShipmentInboundType } from 'schemas/shipment/inbound/detail';
import { COLOR } from '@styles/theme';
import { PalletService, ToastMessage } from '@utility/index';
import {
  DEFAULT_LABEL_FORM_VALUES,
  InboundPalletLabelOptionsForm,
  type LabelFormValues,
} from './InboundPalletLabelOptionsForm';

const { Text } = Typography;

type ShipmentPallet = WmsShipmentInboundType['pallets'][number];

type ModalStep = 'select' | 'labels' | 'done';

type InboundGeneratePalletLabelsModalProps = {
  open: boolean;
  onClose: () => void;
  warehouseId: string;
  pallets: ShipmentPallet[];
  onCompleted?: () => void;
};

const STEP_ITEMS = [
  { title: 'Select pallets' },
  { title: 'Label options' },
  { title: 'Done' },
];

const PALLET_LIST_MAX_HEIGHT = 220;

export function InboundGeneratePalletLabelsModal({
  open,
  onClose,
  warehouseId,
  pallets,
  onCompleted,
}: InboundGeneratePalletLabelsModalProps) {
  const { user } = useAuth();
  const [step, setStep] = useState<ModalStep>('select');
  const [loading, setLoading] = useState(false);
  const [selectedPalletNumbers, setSelectedPalletNumbers] = useState<string[]>([]);
  const [labelForm] = Form.useForm<LabelFormValues>();

  const palletOptions = useMemo(
    () =>
      pallets
        .filter(p => p.palletNumber)
        .map(p => ({ key: p._id || p.palletNumber, value: p.palletNumber })),
    [pallets],
  );

  const allPalletNumbers = useMemo(
    () => palletOptions.map(p => p.value),
    [palletOptions],
  );

  const resetState = useCallback(() => {
    setStep('select');
    setSelectedPalletNumbers(allPalletNumbers);
    labelForm.resetFields();
    labelForm.setFieldsValue(DEFAULT_LABEL_FORM_VALUES);
  }, [allPalletNumbers, labelForm]);

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, resetState]);

  useEffect(() => {
    if (open) {
      setSelectedPalletNumbers(allPalletNumbers);
    }
  }, [open, allPalletNumbers]);

  useEffect(() => {
    if (step === 'labels') {
      labelForm.setFieldsValue(DEFAULT_LABEL_FORM_VALUES);
    }
  }, [step, labelForm]);

  const stepIndex = useMemo(() => {
    if (step === 'select') return 0;
    if (step === 'labels') return 1;
    return 2;
  }, [step]);

  const handleClose = () => {
    if (loading) return;
    onCompleted?.();
    onClose();
  };

  const handleSelectContinue = (): boolean => {
    if (!selectedPalletNumbers.length) {
      ToastMessage.error('Select at least one pallet.');
      return false;
    }
    setStep('labels');
    return false;
  };

  const handleGenerateLabels = async (): Promise<boolean> => {
    if (!selectedPalletNumbers.length) {
      ToastMessage.error('Select at least one pallet.');
      return false;
    }

    try {
      const values = await labelForm.validateFields();
      setLoading(true);

      await PalletService.generatePalletLabels({
        warehouseId,
        palletNumbers: selectedPalletNumbers,
        labelSize: values.labelSize,
        pageSize: values.pageSize,
        labelFormat: values.labelFormat,
      });

      setStep('done');
      return false;
    } catch (error) {
      if (error && typeof error === 'object' && 'errorFields' in error) {
        return false;
      }
      ToastMessage.apiError(error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleOk = async (): Promise<void | boolean> => {
    if (step === 'done') {
      handleClose();
      return;
    }
    if (step === 'select') {
      return handleSelectContinue();
    }
    return handleGenerateLabels();
  };

  const modalTitle = useMemo(() => {
    if (step === 'select') return 'Generate labels';
    if (step === 'labels') return 'Label options';
    return 'Labels requested';
  }, [step]);

  return (
    <Modal
      title={modalTitle}
      open={open}
      centered
      onCancel={handleClose}
      destroyOnHidden
      confirmLoading={loading}
      okText={
        step === 'select' ? 'Continue' : step === 'labels' ? 'Generate labels' : 'Close'
      }
      cancelText={step === 'done' ? undefined : 'Cancel'}
      cancelButtonProps={step === 'done' ? { style: { display: 'none' } } : undefined}
      onOk={handleOk}
    >
      <Steps
        current={stepIndex}
        items={STEP_ITEMS}
        size="small"
        style={{ marginBottom: 24 }}
      />

      {step === 'select' && (
        <Flex vertical gap={8}>
          <Flex align="center" justify="space-between">
            <Text type="secondary" style={{ fontSize: 12 }}>
              {selectedPalletNumbers.length} of {allPalletNumbers.length} selected
            </Text>
            <Flex gap={8}>
              <Typography.Link
                style={{ fontSize: 12 }}
                onClick={() => setSelectedPalletNumbers(allPalletNumbers)}
              >
                All
              </Typography.Link>
              <Typography.Link
                style={{ fontSize: 12 }}
                onClick={() => setSelectedPalletNumbers([])}
              >
                None
              </Typography.Link>
            </Flex>
          </Flex>
          <div
            style={{
              maxHeight: PALLET_LIST_MAX_HEIGHT,
              overflowY: 'auto',
              border: `1px solid ${COLOR.border}`,
              borderRadius: 6,
              padding: '8px 12px',
            }}
          >
            <Checkbox.Group
              value={selectedPalletNumbers}
              onChange={values => setSelectedPalletNumbers(values as string[])}
              style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}
            >
              {palletOptions.map(option => (
                <Checkbox
                  key={option.key}
                  value={option.value}
                  style={{ fontSize: 13, marginInlineStart: 0 }}
                >
                  {option.value}
                </Checkbox>
              ))}
            </Checkbox.Group>
          </div>
        </Flex>
      )}

      {step === 'labels' && <InboundPalletLabelOptionsForm form={labelForm} />}

      {step === 'done' && (
        <Text>
          Your labels are being prepared and will be sent to{' '}
          <Text strong>{user?.email ?? 'your email'}</Text> when ready.
        </Text>
      )}
    </Modal>
  );
}
